export type CaptchaPlan = 'none' | 'token' | 'sandbox-legacy' | 'blocked';

/**
 * How the form satisfies the tenant's reCAPTCHA requirement.
 *
 * - `none` — tenant has the captcha disabled: POST /bookings with an empty token
 *   (vendor-faithful; their widget does the same).
 * - `token` — captcha required and a site key is configured: render the widget,
 *   submit its token.
 * - `sandbox-legacy` — captcha required but there is no site key AND we are on the
 *   sandbox tenant: submit through the documented captcha-less `POST /booking`
 *   instead. This is a testing affordance only — the env gate here plus the client's
 *   sandbox host interlock make it unreachable in production, where the
 *   "always the captcha-protected /bookings" rule stands. (Background: the vendor's
 *   own site key is domain-locked to launch27.com, so a captcha the sandbox demands
 *   can never be rendered on the WordPress site's domain.)
 * - `blocked` — captcha required, no site key, production: submission is impossible;
 *   the payment section surfaces the existing "temporarily unavailable" notice.
 */
/**
 * Which endpoint creates the booking.
 *
 * `POST /booking` is the documented one and the one the vendor's OWN widget uses
 * (`$resource('bookings', {}, { create: { url: 'booking' } })`, re-confirmed 2026-09-11); it takes
 * no captcha token. `POST /bookings` is the undocumented captcha-protected variant whose schema
 * demands a token of at least one character — so it can only be used when a real token exists.
 *
 * This tenant has the captcha disabled (`recaptcha_disable.default_booking === 1`), so the form
 * sent `recaptcha_token: ""` to `/bookings` and every live booking was rejected 422
 * "String length must be greater than or equal to 1". Hence: token in hand → `/bookings`,
 * otherwise `/booking`, exactly as the vendor widget behaves.
 */
export function bookingEndpoint(plan: CaptchaPlan): '/booking' | '/bookings' {
  return plan === 'token' ? '/bookings' : '/booking';
}

export function captchaPlan(
  required: boolean,
  siteKey: string | null,
  env: 'sandbox' | 'production',
): CaptchaPlan {
  if (!required) return 'none';
  if (siteKey) return 'token';
  return env === 'sandbox' ? 'sandbox-legacy' : 'blocked';
}
